import {
  addPricingMappingCodeField,
  getDefaultCouponState,
  handleCouponErrMsg,
  isFromMarketingCampaign,
} from '@/views/config/coupon/helper/utils';
import {
  couponDiscountTypeMap,
  couponFetchStatus,
  couponFormModeMap,
  couponSourceTypeMap,
  couponTypes,
  FLOW_TYPE_KEY,
  PLAN_TYPE_KEY,
} from '@/views/config/coupon/helper/constant';
import { getMultiCouponPlanList, validateCoupon, verifyCoupon } from '@/apis/coupon';
import _, { filter, find, get, omitBy, some, values } from 'lodash';
import { dateTimeFormatWithClientZone, getEarliestTime } from '@/utils/dateformat';
import { ALLOWED_PAID_PLAN_IN_BUYTRIAL, AUDIO_SUB_TYPES, ENUM_ZUORA_COUPON_TYPE, planTypes } from '@/common/constants';
import { isEmptyVal } from '@/utils';
import {
  convertCampaignCouponResult2State,
  convertPartnerReferCouponResult2State,
  convertSubscriptionResult2State,
  convertVerifyCouponResult2State,
} from '@/views/config/coupon/helper/result-to-state';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';
import { CouponErrorHandler } from '@/views/config/coupon/helper/error-handler';
import { logger } from '@/utils/log';
import { parseTncMessage } from '@/views/config/coupon/helper/parse-tnc';
import { computeDiscountWithAmountMap, computedNextBillAmount } from '@/views/config/coupon/helper/compute';
import { filterOutFreeTrialPayloads } from '@/common/service/free-trial';
import { plansof } from '@/utils/chain';
import { checkPlanDownSellForEdit } from '@/common/service/validate/downsell';
import { checkCurrentIsEditA2MCases } from '@/views/config/coupon/helper/validator';
import { SupportCouponModeMap } from '@/views/config/coupon/component/support-coupon/constant';
import { shoppingCartPlanInitReady } from '@/utils/ready';

const handlerCouponError = (couponResult = {}, planList = []) => {
  let { errorMessage = '' } = couponResult || {};

  if (isEmptyVal(errorMessage)) {
    const couponErrorHandler = new CouponErrorHandler(couponResult, planList);
    return couponErrorHandler.getErrorMsgWithErrorCode();
  }

  return errorMessage;
};

const getUsageTypeParams = (usageTypeFromForm) => {
  if (usageTypeFromForm) {
    return usageTypeFromForm;
  }

  const usageTypeFromQuery = window.$query?.usageType;

  // only for new subscription will reference usageType param from link
  if ([flowTypes.buyNow, flowTypes.basic2pro].includes(pageConfig?.type) && usageTypeFromQuery) {
    return usageTypeFromQuery;
  }

  return '';
}

export const COUPON_ACTION_TYPES = {
  verifyCoupon: 'verify/coupon',
  showTCMessage: 'show/coupon/tnc/message',
  removeCoupon: 'remove/coupon',
  preCheckCoupon: 'pre/check/coupon',
  initCouponState: 'init/coupon/state',
};

const state = {
  couponState: getDefaultCouponState(),
  couponMessage: '',
  verifyCouponStatus: couponFetchStatus.idle,
  couponFormMode: couponFormModeMap.INIT,
  multiCouponPlanList: [],
  supportCouponState: { // This is a special coupon type that applies to all discount rate plans defined in zuora and does not distinguish between processes.
    discountPercentage: window.newBillingConfig?.supportCoupon?.discount,
    couponCode: window.newBillingConfig?.supportCoupon?.instanceId,
    couponDisplayMode: SupportCouponModeMap.unset,
    unSupportedCouponCase: false,
  },
};

const getters = {
  supportCouponState: (state) => state.supportCouponState,
  supportCouponCode: (state) => state.supportCouponState.couponCode,
  useSpecialSupportCoupon: (state, getters) => {
    const useSupportCoupon = !!getters.supportCouponCode;

    // do not have support coupon in current account
    if (!useSupportCoupon) return false;

    // only for edit flow with support coupon in phase one
    if (![flowTypes.edit].includes(pageConfig?.type)) {
      logger.info('support coupon only for edit flow with support coupon in phase one!');
      return false;
    }

    // for some ineligible cases with support coupon
    if (isFromMarketingCampaign()) {
      logger.info('support coupon ineligible for campaign cases!');
      return false;
    }

    return true;
  },
  haveVerifiedSupportCoupon: (state, getters) => {
    return getters.useSpecialSupportCoupon && getters.supportCouponState.couponDisplayMode === SupportCouponModeMap.form;
  },
  couponFingerPrint: (state, getters) => {
    if (getters.useSpecialSupportCoupon) {
      return getters.couponState.couponCode;
    }

    return '';
  },
  couponState: (state) => state.couponState,
  couponMessage: (state) => state.couponMessage,
  verifyCouponStatus: (state) => state.verifyCouponStatus,
  timebaseEarlyExpireDate: (_, getters) => {
    const { couponType, couponSourceType } = getters.couponState || {};

    if (couponType === couponTypes.MARKETING || couponSourceType === couponSourceTypeMap.threshold) {
      const discountPlans = getters.discountWithAmountMap.discountPlans;
      return getEarliestTime(filter(values(discountPlans), (plan) => !!plan.discountAmount));
    }

    const timeBase = getters.couponState.timeBase;
    const selectedPlan = getters.getSelectedPlansMap;

    return getEarliestTime(filter(timeBase, (item) => {
      return isEmptyVal(selectedPlan[item.plan_code]) === false;
    }));
  },
  tncCouponEarlyExpireDate: (_, getters) => {
    return dateTimeFormatWithClientZone(getters.timebaseEarlyExpireDate);
  },
  // next billing amount, need compare current plan's promotion expire date and next billing cycle date
  nextBillAmount: () => computedNextBillAmount(),
  tncMessage: () => parseTncMessage(),
  // Compute all collections of products with discounts
  discountWithAmountMap: () => computeDiscountWithAmountMap(),
  getEffectSelectedPlansMap: (state, getters) => {
    const selectedPlansMap = getters.getSelectedPlansMap;
    const inrAudioPlan = find(getters.getTypeGroupSelectedPlans[planTypes.TOLLFREE], (item) => {
      return item.category === AUDIO_SUB_TYPES.INDIA_AUDIO;
    });

    if (inrAudioPlan) {
      // filter biz + inr audio plan, avoid calculate discount with threshold coupon
      return omitBy(selectedPlansMap, (item) => {
        return item.type === planTypes.ZOOM_MEETING && item.category === ZOOM_MEETING_SUB_TYPES.BIZ;
      });
    } else {
      return selectedPlansMap;
    }
  },
  couponFormMode: (state) => state.couponFormMode,
  forNewSubscriptionFlow: (state, getters) => {
    const isTrialFlowWithSubscription = pageConfig?.type === flowTypes.buyTrial &&
      plansof(getters.getSelectedPlans).type(...ALLOWED_PAID_PLAN_IN_BUYTRIAL).not().freeTrial().has()
    return [flowTypes.basic2pro, flowTypes.buyNow].includes(pageConfig?.type) || isTrialFlowWithSubscription
  },
  fromCampaignLink: (state, getters) => {
    const { couponSourceType, couponType } = state.couponState;

    // for net new Customer or free account
    if (getters.forNewSubscriptionFlow) {
      if ((couponType === couponTypes.MARKETING
        && ![couponSourceTypeMap.preload, couponSourceTypeMap.threshold].includes(couponSourceType))
        || couponSourceTypeMap.marketing === couponSourceType) {
        return true;
      }
    } else if (couponType === couponTypes.MARKETING) {
      return true;
    }

    return false;
  },
  isFromCancelCampaign: (state) => {
    const { couponType, discountInfo } = state.couponState;

    // OBT-4969: Hide coupon section for save offer from cancellation flow
    if (
      couponType === couponTypes.MARKETING &&
      Object.values(discountInfo).some((i) => i.couponDefType === ENUM_ZUORA_COUPON_TYPE.CANCELLATION)) {
      return true;
    }
    return false;
  },
  isReplaceSubscriptionMarketingCoupon: (state, getters) => {
    return getters.fromCampaignLink && !getters.forNewSubscriptionFlow;
  },
  multiCouponPlanList: (state) => state.multiCouponPlanList,
  isMultipleCoupon: (state, getters) => {
    const existDiscount = some(getters.subscriptions, (p) => p?.discount > 0);
    return existDiscount && getters.fromCampaignLink;
  },
  haveAppliedThresholdCoupon: (state, getters) => {
    return some(getters.subscriptions, (p) => p?.couponDiscountType === couponDiscountTypeMap.thresholdAmount);
  },
  mixBuyPlanType4FlowType: (state, getters) => { /// key -> flow Type, value -> planType list
    const marketingCoupon = pageConfig?.partnerReferral?.coupon || pageConfig?.marketingCoupon;

    if (isEmptyVal(marketingCoupon)) return {};

    const { discountInfo, discountInfoExtension } = marketingCoupon;
    const allDiscountInfo = {...discountInfo, ...discountInfoExtension };
    const groupByPlanCodePlanMap = getters.groupByPlanCodePlanMap;

    return _.chain(allDiscountInfo)
      .keys()
      .map((planCode) => {
        const plan = _.get(groupByPlanCodePlanMap, [planCode]);
        const discountInfo = _.get(allDiscountInfo, [planCode]);
        return plan ? {...plan, ...discountInfo } : undefined;
      })
      .filter(Boolean)
      .groupBy(FLOW_TYPE_KEY)
      .mapValues((planList) => {
        return _.chain(planList)
          .map(PLAN_TYPE_KEY)
          .uniq()
          .value();
      })
      .value();
  },
  // for mix buy flow, exist edit plan case (eg: edit zoom one pro/biz + add addon plan)
  mixBuyFlowContainsEditCase: (state, getters) => {
    if (pageConfig?.type !== flowTypes.mixBuy) return false;
    return get(getters.mixBuyPlanType4FlowType, [flowTypes.edit], []).length > 0;
  },
  accountWithCouponDiscount: (state, getters) => {
    // account apply a coupon in shopping cart
    if (getters.discountWithAmountMap?.totalDiscount > 0) {
      return true;
    }

    // account have forever coupon
    if (getters.basicConfig?.hasAccountDiscount) {
      return true;
    }

    // account is in multiple coupon case
    if (getters.isMultipleCoupon) {
      return true;
    }

    // account have threshold coupon
    if (getters.haveAppliedThresholdCoupon) {
      return true;
    }
  },
};

const mutations = {
  setSupportCouponState(state, data = {}) {
    Object.keys(data).forEach((key) => {
      state.supportCouponState[key] = data[key];
    });
  },
  setCouponState(state, couponState) {
    state.couponState = couponState;
  },
  setCouponMessage(state, message) {
    state.couponMessage = message;
  },
  resetCouponState(state) {
    state.couponState = getDefaultCouponState();
    state.verifyCouponStatus = couponFetchStatus.idle;
  },
  setVerifyCouponStatus(state, verifyCouponStatus) {
    state.verifyCouponStatus = verifyCouponStatus;
  },
  setCouponFormMode(state, data) {
    state.couponFormMode = data;
  },
  setMultiCouponPlanList(state, data = []) {
    state.multiCouponPlanList = data;
  },
};

const actions = {
  async checkUnsupportedCouponCase({ commit, getters }) {
    if (window.$context.is.edit && window.$isEnabled("blockSupportCouponWithA2MAndDownsell")) {
      try {
        await checkPlanDownSellForEdit(true);
        commit('setSupportCouponState', { unSupportedCouponCase: false });
      } catch (e) {
        commit('setSupportCouponState', { unSupportedCouponCase: true });
      }

      if (!getters.supportCouponState?.unSupportedCouponCase) {
        commit('setSupportCouponState', { unSupportedCouponCase: checkCurrentIsEditA2MCases() });
      }
    }
  },
  async verifySupportCoupon({ dispatch, getters }, payload) {
    // block downsell and A2M case (just for frontend block)
    await dispatch('checkUnsupportedCouponCase');
    const { unSupportedCouponCase } = getters.supportCouponState;
    const unSupportedErrorCode = '379999';

    if (unSupportedCouponCase) {
      const errorMessage = handlerCouponError({ errorCode: unSupportedErrorCode });
      await dispatch('setCouponFailState', { errorMessage, errorCode: unSupportedErrorCode });
    } else {
      await dispatch(COUPON_ACTION_TYPES.verifyCoupon, payload);
    }
  },
  setCouponFailState({ commit }, payload = {}) {
    commit('setCouponState', handleCouponErrMsg(payload));
    commit('setVerifyCouponStatus', couponFetchStatus.failure);
  },
  async [COUPON_ACTION_TYPES.verifyCoupon]({ getters, commit, dispatch }, payload = {}) {
    const { couponCode = '' } = payload;

    if (isEmptyVal(couponCode)) return Promise.resolve();

    commit('setVerifyCouponStatus', couponFetchStatus.pending);

    try {
      const result = await verifyCoupon({
        orderData: {
          planList: filterOutFreeTrialPayloads(getters.selectedPlanListPayLoad),
          coupon: {
            fromCampaignLink: false,
            code: couponCode,
          },
        },
        extension: {
          soldToCountry: getters.soldToContact?.country,
          jointsaleCode: getters.partnerRefer?.jointsaleCode,
          usageType: getUsageTypeParams(getters.addressInfo?.usageType),
        },
      });

      commit('setCouponState', convertVerifyCouponResult2State(result, { couponCode }));
      commit('setVerifyCouponStatus', couponFetchStatus.successfully);
    } catch (couponResult) {
      logger.error('verify coupon api', couponResult);
      const errorMessage = handlerCouponError(couponResult, getters.planMapFlat);
      dispatch('setCouponFailState', { errorMessage, errorCode: couponResult?.errorCode });
    }
  },
  async [COUPON_ACTION_TYPES.preCheckCoupon]({ getters, commit, dispatch }, payload = {}) {
    try {
      const { couponCode = '' } = payload;

      if (isEmptyVal(couponCode)) return Promise.resolve();

      commit('setVerifyCouponStatus', couponFetchStatus.pending);

      const oldPlanType = window.$config("oldPlanType");

      const couponPayload = getters.couponPayload;

      await validateCoupon({
        orderData: {
          planList: filterOutFreeTrialPayloads(getters.selectedPlanListPayLoad),
          coupon: {
            ...couponPayload,
            code: couponCode,
          },
        },
        extension: {
          soldToCountry: getters.soldToContact?.country,
          jointsaleCode: getters.partnerRefer?.jointsaleCode,
          usageType: getUsageTypeParams(getters.addressInfo?.usageType),
        },
        oldPlanType,
      });

      commit('setVerifyCouponStatus', couponFetchStatus.successfully);
    } catch (couponResult) {
      logger.error('validate coupon api', couponResult);
      const errorMessage = handlerCouponError(couponResult, getters.planMapFlat);
      const errorResult = { errorMessage, errorCode: couponResult?.errorCode };

      // for Net new customer or free account, when the current coupon valid fail, should remove coupon state, add error message and set fail status
      if (getters.forNewSubscriptionFlow) {
        dispatch('setCouponFailState', errorResult);
      } else {
        // for edit flow/add addon flow, Need to keep coupon related information, just add error message and set fail status
        commit('setCouponState', {...getters.couponState, ...errorResult });
        commit('setVerifyCouponStatus', couponFetchStatus.failure);
      }
    }
  },
  async updateReplaceSubscriptionList({ getters, commit }) {
    const couponState = getters.couponState;
    // just for edit base/addon && upsell addon cases
    // when trigger replace subscription with marketing coupon
    if ([flowTypes.edit, flowTypes.add2cart].includes(pageConfig.type) && couponState?.fromPreloadLink) {
      try {
        const { result = [] } = await getMultiCouponPlanList();
        commit("setMultiCouponPlanList", addPricingMappingCodeField(result));
      } catch (e) {
        logger.error("api fetch error", e);
      }
    }
  },
  async doCampaignAction({ commit, dispatch }) {
    commit('setCouponState', convertCampaignCouponResult2State(pageConfig?.marketingCoupon));
    commit('setVerifyCouponStatus', couponFetchStatus.successfully);
    dispatch('updateReplaceSubscriptionList');
  },
  async doPreloadCartIdAction({ dispatch }) {
    const { orderData } = pageConfig?.preloadCartInfo || {};
    // The coupon parameter has a higher priority than the one in cartId.
    const couponCode = pageConfig?.coupon || orderData?.coupon?.code;
    dispatch(COUPON_ACTION_TYPES.verifyCoupon, { couponCode });
  },
  async doPartnerReferAction({ commit }) {
    const { coupon } = pageConfig.partnerReferral || {};
    commit('setCouponState', convertPartnerReferCouponResult2State(coupon));
    commit('setVerifyCouponStatus', couponFetchStatus.successfully);
  },
  async doCouponSessionAction({ getters, dispatch }) {
    dispatch(COUPON_ACTION_TYPES.verifyCoupon, { couponCode: getters.couponState?.couponCode });
  },
  async doSpecialSupportCouponAction({ getters, dispatch, commit }) {
    commit('setSupportCouponState', { couponDisplayMode: SupportCouponModeMap.form });
    // This is mainly to solve the problem that the initialization of some products is not performed in the initShoppingCart method, but in the current product component.
    shoppingCartPlanInitReady.ready(() => {
      dispatch('verifySupportCoupon', { couponCode: getters.supportCouponCode });
    });
  },
  async doEditSubscriptionAction({ getters, commit }) {
    const timeBase = convertSubscriptionResult2State(getters.subscriptions);

    if (timeBase.length) {
      commit('setCouponState', {
        ...getDefaultCouponState(),
        timeBase,
        couponType: couponTypes.TIMEBASE,
      });
    }
  },
  async doCouponQueryParamAction({ dispatch }) {
    dispatch(COUPON_ACTION_TYPES.verifyCoupon, { couponCode: pageConfig?.coupon });
  },
  async doCouponPersistQueryParamAction({ dispatch, commit }) {
    dispatch(COUPON_ACTION_TYPES.verifyCoupon, { couponCode: pageConfig?.persistCoupon });
    commit('setCouponFormMode', couponFormModeMap.EDIT);
  },
  async [COUPON_ACTION_TYPES.initCouponState]({ dispatch, getters }) {
    // for preload cart info with delta (add quantity with product)
    if (window.$context?.is?.delta) return;

    const { marketingCoupon, preloadCartInfo, partnerReferral, type: flowType } = pageConfig;

    // first check special coupon code, when the purchase link is not from campaign link (marketing campaign/partner etc...)
    if (getters.useSpecialSupportCoupon) {
      logger.info('init support coupon state: for special support coupon with coupon state!');
      return dispatch('doSpecialSupportCouponAction');
    }

    // priority is first check session
    if (getters.forNewSubscriptionFlow && !isEmptyVal(getters.couponState.couponCode)) {
      logger.info("init coupon state: for session store cache with coupon state");
      return dispatch("doCouponSessionAction");
    }

    // persist coupon code with switch browser link
    if (getters.forNewSubscriptionFlow && pageConfig.persistCoupon && isEmptyVal(pageConfig?.couponErrorResult)) {
      logger.info("init coupon state: for persist coupon code with switch url");
      return dispatch("doCouponPersistQueryParamAction");
    }

    if (marketingCoupon) {
      logger.info("init coupon state: for campaign link [/buy/campaign, /billing/campaign, /billing/campaign/cancel]");
      return dispatch('doCampaignAction');
    }

    if (!isEmptyVal(preloadCartInfo) && getters.forNewSubscriptionFlow) {
      logger.info("init coupon state: for share shopping cart with cartId");
      return dispatch('doPreloadCartIdAction');
    }

    if (partnerReferral?.coupon) {
      logger.info("init coupon state: for partner refer with coupon case");
      return dispatch('doPartnerReferAction');
    }

    // when page init by coupon validation with backend return couponErrorResult, don't need call coupon api
    if (getters.forNewSubscriptionFlow && pageConfig?.coupon && isEmptyVal(pageConfig?.couponErrorResult)) {
      logger.info("init coupon state: for query with coupon parameter");
      return dispatch("doCouponQueryParamAction");
    }

    if ([flowTypes.edit, flowTypes.add2cart].includes(flowType)) {
      logger.info(`init coupon state: for ${flowType} subscription with origin coupon`);
      return dispatch("doEditSubscriptionAction");
    }
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
